/**
 * MeetingForm — single UI component for create, edit, and detail (read-only).
 * Used by MeetingFormPage (create/edit) and MeetingDetailPage (detail).
 */
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormRoot,
  Input,
  Label,
  toast,
  zodResolver,
} from "@devhop/ui";
import { Plus, Sparkles, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import type { MeetingDetailDTO } from "@/modules/management/meeting-minutes/domain/types";
import { config } from "@/shared/lib/config";
import { formatDateForInput, formatDateLocal } from "@/shared/lib/date-time";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormTipTapEditor } from "@/shared/ui/FormTipTapEditor";
import { meetingFormSamples } from "./meetingFormSamples";

const isDev =
  typeof import.meta !== "undefined" &&
  (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

export const meetingFormSchema = z.object({
  meetingDate: z.string().min(1, "ກະລຸນາເລືອກວັນທີ"),
  title: z.string().min(1, "ກະລຸນາໃສ່ຫົວຂໍ້"),
  summary: z.string().optional().nullable(),
  attendees: z.array(
    z.object({
      userId: z.string().min(1),
      role: z.string().optional().nullable(),
    }),
  ),
  actions: z.array(
    z.object({
      description: z.string().min(1, "ລາຍລະອຽດປະເດັນ"),
      assigneeUserIds: z.array(z.string()).default([]),
      dueDate: z.string().optional().nullable(),
    }),
  ),
});

export type MeetingFormValues = z.infer<typeof meetingFormSchema>;

export const defaultMeetingFormValues: MeetingFormValues = {
  meetingDate: formatDateForInput(new Date()),
  title: "",
  summary: "",
  attendees: [],
  actions: [],
};

function actionStatusLabel(status: string): string {
  return status === "done" ? "ສຳເລັດ" : "ລໍຖ້າ";
}

type MeetingFormProps =
  | {
      mode: "create" | "edit";
      initialValues?: Partial<MeetingFormValues>;
      onSubmit: (values: MeetingFormValues) => void | Promise<void>;
      onCancel: () => void;
      isSubmitting?: boolean;
      meeting?: never;
    }
  | {
      mode: "detail";
      meeting: MeetingDetailDTO;
      initialValues?: never;
      onSubmit?: never;
      onCancel?: () => void;
      isSubmitting?: never;
    };

export function MeetingForm(props: MeetingFormProps) {
  const { mode } = props;
  const onCancel = "onCancel" in props ? props.onCancel : undefined;

  const methods = RHF.useForm<MeetingFormValues>({
    resolver: zodResolver(meetingFormSchema),
    defaultValues:
      mode !== "detail"
        ? { ...defaultMeetingFormValues, ...props.initialValues }
        : defaultMeetingFormValues,
  });

  const { watch, setValue, reset } = methods;
  const attendees = watch("attendees");
  const actions = watch("actions");

  useEffect(() => {
    if (mode === "edit" && props.initialValues) {
      reset({ ...defaultMeetingFormValues, ...props.initialValues });
    }
  }, [mode, props.initialValues, reset]);

  const [sampleIndex, setSampleIndex] = useState(0);

  if (mode === "detail") {
    const meeting = props.meeting;
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>ຂໍ້ມູນທົ່ວໄປ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-xs">ວັນທີປະຊຸມ</Label>
              <p className="text-sm">{formatDateLocal(meeting.meetingDate)}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">ຫົວຂໍ້</Label>
              <p className="text-sm">{meeting.title}</p>
            </div>
            {meeting.summary && (
              <div>
                <Label className="text-muted-foreground text-xs">ສະຫຼຸບ</Label>
                <div
                  className="prose prose-sm dark:prose-invert mt-1 max-w-none text-sm [&_ol]:list-decimal [&_ul]:list-disc"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: Summary is trusted HTML from TipTap editor
                  dangerouslySetInnerHTML={{ __html: meeting.summary }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ຜູ້ຮ່ວມ ({meeting.attendees.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {meeting.attendees.length === 0 ? (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຜູ້ຮ່ວມ</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {meeting.attendees.map((a) => (
                  <li key={a.id}>
                    {a.userName ?? a.userEmail ?? a.userId}
                    {a.role && (
                      <span className="ml-2 text-muted-foreground">
                        ({a.role})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ປະເດັນທີ່ລົມກັນ ({meeting.actions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {meeting.actions.length === 0 ? (
              <p className="text-muted-foreground text-sm">ບໍ່ມີປະເດັນ</p>
            ) : (
              <div className="space-y-3">
                {meeting.actions.map((a) => (
                  <div
                    key={a.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm"
                  >
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none flex-1 [&_ol]:list-decimal [&_ul]:list-disc"
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: Action description is trusted HTML from TipTap editor
                      dangerouslySetInnerHTML={{ __html: a.description }}
                    />
                    <div className="flex items-center gap-2">
                      {a.assignees.length > 0 && (
                        <span className="text-muted-foreground">
                          ຜູ້ຮັບຜິດຊອບ:{" "}
                          {a.assignees
                            .map((x) => x.userName ?? x.userId)
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      )}
                      {a.dueDate && (
                        <span className="text-muted-foreground">
                          ວັນສົ່ງ: {formatDateLocal(a.dueDate)}
                        </span>
                      )}
                      <Badge
                        variant={a.status === "done" ? "default" : "secondary"}
                      >
                        {actionStatusLabel(a.status)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {props.mode === "detail" && props.onCancel && (
          <Button variant="outline" onClick={props.onCancel}>
            ກັບຄືນ
          </Button>
        )}
      </div>
    );
  }

  const handleAutoFill = () => {
    const sample = meetingFormSamples[sampleIndex];
    if (sample) {
      setValue("meetingDate", sample.meetingDate);
      setValue("title", sample.title);
      setValue("summary", sample.summary ?? "");
      setValue(
        "actions",
        sample.actions.map((a) => ({
          description: a.description,
          assigneeUserIds: [],
          dueDate: a.dueDate ?? "",
        })),
      );
      setSampleIndex((prev) => (prev + 1) % meetingFormSamples.length);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາເລືອກຜູ້ຮ່ວມ (ຖ້າຕ້ອງການ).");
    }
  };

  const addAttendee = () =>
    setValue("attendees", [...attendees, { userId: "", role: "" }]);
  const removeAttendee = (i: number) =>
    setValue(
      "attendees",
      attendees.filter((_, idx) => idx !== i),
    );
  const addAction = () =>
    setValue("actions", [
      ...actions,
      { description: "", assigneeUserIds: [], dueDate: "" },
    ]);
  const addActionAssignee = (actionIdx: number) => {
    const cur = actions[actionIdx]?.assigneeUserIds ?? [];
    setValue(`actions.${actionIdx}.assigneeUserIds`, [...cur, ""]);
  };
  const removeActionAssignee = (actionIdx: number, assigneeIdx: number) => {
    const cur = actions[actionIdx]?.assigneeUserIds ?? [];
    setValue(
      `actions.${actionIdx}.assigneeUserIds`,
      cur.filter((_, j) => j !== assigneeIdx),
    );
  };
  const removeAction = (i: number) =>
    setValue(
      "actions",
      actions.filter((_, idx) => idx !== i),
    );

  return (
    <FormRoot<MeetingFormValues>
      methods={methods}
      onSubmit={(values) => {
        void props.onSubmit(values);
      }}
    >
      <div className="space-y-6 pb-20">
        <Card>
          <CardHeader>
            <CardTitle>ຂໍ້ມູນທົ່ວໄປ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label>ວັນທີປະຊຸມ *</Label>
              <RHF.Controller
                control={methods.control}
                name="meetingDate"
                render={({ field }) => (
                  <Input type="date" {...field} className="max-w-xs" />
                )}
              />
            </div>
            <div className="grid gap-2">
              <Label>ຫົວຂໍ້ *</Label>
              <RHF.Controller
                control={methods.control}
                name="title"
                render={({ field }) => (
                  <Input placeholder="ຫົວຂໍ້ການປະຊຸມ" {...field} />
                )}
              />
            </div>
            <div className="grid gap-2">
              <FormTipTapEditor
                name="summary"
                label="ສະຫຼຸບ"
                placeholder="ສະຫຼຸບເນື້ອໃນການປະຊຸມ"
                className="min-h-[120px]"
                toolbarElements={[
                  "bold",
                  "italic",
                  "strikethrough",
                  "bulletList",
                  "orderedList",
                  "blockquote",
                  "alignLeft",
                  "alignCenter",
                  "alignRight",
                  "alignJustify",
                  "undo",
                  "redo",
                ]}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>ຜູ້ຮ່ວມ</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAttendee}
            >
              <Plus className="mr-1 h-4 w-4" />
              ເພີ່ມ
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {attendees.map((a, i) => (
              <div
                key={
                  a.userId ? `attendee-${a.userId}-${i}` : `attendee-new-${i}`
                }
                className="flex flex-wrap items-end gap-2"
              >
                <div className="min-w-[200px] flex-1">
                  <FormInfiniteCombobox<LookupItem>
                    name={`attendees.${i}.userId`}
                    label="ຜູ້ໃຊ້"
                    queryKey={["management-meeting-user-lookup"]}
                    queryFn={(args) =>
                      fetchLookupForInfinite(
                        `${config.apiUrl}/users/lookup`,
                        args,
                      )
                    }
                    preloadQueryFn={(userId) =>
                      hydrateLookupItem(`${config.apiUrl}/users/lookup`, userId)
                    }
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    placeholder="ເລືອກຜູ້ຮ່ວມ"
                    clearable
                  />
                </div>
                <RHF.Controller
                  control={methods.control}
                  name={`attendees.${i}.role`}
                  render={({ field }) => (
                    <Input
                      placeholder="ບົດບາດ (ຕົວເລືອກ)"
                      className="w-40"
                      {...field}
                      value={field.value ?? ""}
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAttendee(i)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {attendees.length === 0 && (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີຜູ້ຮ່ວມ. ກົດ ເພີ່ມ ເພື່ອເພີ່ມແຖວ.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>ປະເດັນທີ່ລົມກັນ</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAction}
            >
              <Plus className="mr-1 h-4 w-4" />
              ເພີ່ມ
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {actions.map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: form action items have no id; index stable until add/remove
                key={`action-${i}`}
                className="space-y-2 rounded-lg border p-3"
              >
                <FormTipTapEditor
                  name={`actions.${i}.description`}
                  placeholder="ລາຍລະອຽດປະເດັນ *"
                  className="min-h-[80px]"
                  toolbarElements={[
                    "bold",
                    "italic",
                    "strikethrough",
                    "bulletList",
                    "orderedList",
                    "blockquote",
                    "alignLeft",
                    "alignCenter",
                    "alignRight",
                    "alignJustify",
                    "undo",
                    "redo",
                  ]}
                />
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label className="text-sm">ຜູ້ຮັບຜິດຊອບ</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addActionAssignee(i)}
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      ເພີ່ມ
                    </Button>
                  </div>
                  {(actions[i]?.assigneeUserIds ?? []).length === 0 ? (
                    <p className="text-muted-foreground text-xs">
                      ບໍ່ມີຜູ້ຮັບຜິດຊອບ — ກົດ ເພີ່ມ ເພື່ອເລືອກຄົນ (ຫຼາຍຄົນໄດ້).
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {(actions[i]?.assigneeUserIds ?? []).map((_, j) => (
                        <div
                          // biome-ignore lint/suspicious/noArrayIndexKey: assignee picker rows keyed by stable index within action
                          key={`action-${i}-assignee-${j}`}
                          className="flex flex-wrap items-end gap-2"
                        >
                          <div className="min-w-[180px] flex-1">
                            <FormInfiniteCombobox<LookupItem>
                              name={`actions.${i}.assigneeUserIds.${j}`}
                              label={j === 0 ? undefined : "\u00a0"}
                              queryKey={["management-meeting-assignee-lookup"]}
                              queryFn={(args) =>
                                fetchLookupForInfinite(
                                  `${config.apiUrl}/users/lookup`,
                                  args,
                                )
                              }
                              preloadQueryFn={(userId) =>
                                hydrateLookupItem(
                                  `${config.apiUrl}/users/lookup`,
                                  userId,
                                )
                              }
                              getLabel={(item) => item.name}
                              getValue={(item) => item.id}
                              placeholder="ເລືອກຜູ້ຮັບຜິດຊອບ"
                              clearable
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            onClick={() => removeActionAssignee(i, j)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap items-end gap-2">
                    <RHF.Controller
                      control={methods.control}
                      name={`actions.${i}.dueDate`}
                      render={({ field }) => (
                        <div className="grid gap-2">
                          <Label className="text-sm">ວັນສົ່ງ</Label>
                          <Input
                            type="date"
                            placeholder="ວັນສົ່ງ"
                            className="w-40"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </div>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="ml-auto shrink-0"
                      onClick={() => removeAction(i)}
                      title="ລຶບປະເດັນນີ້"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {actions.length === 0 && (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີປະເດັນ. ກົດ ເພີ່ມ ເພື່ອເພີ່ມແຖວ.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-2 border-t bg-background/80 px-4 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
          {mode === "create" && isDev && meetingFormSamples.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoFill}
              disabled={props.isSubmitting}
              className="gap-1.5"
            >
              <Sparkles className="h-4 w-4" />
              Auto Fill (Sample {sampleIndex + 1}/{meetingFormSamples.length})
            </Button>
          )}
        </div>
        <Button type="submit" disabled={props.isSubmitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
